import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../services/api';

import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import {
  BarChart3,
  ChevronRight
} from 'lucide-react';

const COLORS = [
  '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6',
  '#06B6D4', '#F97316', '#84CC16', '#EC4899', '#6366F1',
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg">
        <p className="text-sm font-semibold">{payload[0].name}</p>
        <p className="text-xs">Count: {payload[0].value}</p>
      </div>
    );
  }
  return null;
};

function Reports() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState({
    campaigns: {
      total: 0,
      active: 0,
      inactive: 0,
      entries: 0,
      totalNewsletterSubscribers: 0,
    },
    entries: {
      total: 0,
      today: 0,
      thisWeek: 0,
      thisMonth: 0,
      byStatus: {
        pending: 0,
        approved: 0,
        rejected: 0,
      },
    },
    forms: {
      total: 0,
      submissions: 0,
      averageFields: 0,
      topForms: [],
    },
  });

  const fetchReportData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/reports');
      const { campaigns, entries, forms } = response.data;
      setReportData((prev) => ({
        campaigns: campaigns ?? prev.campaigns,
        entries: entries ?? prev.entries,
        forms: forms ?? prev.forms,
      }));
      setError('');
    } catch (err) {
      console.error('Fetch report data error:', err);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const StatCard = ({ title, value, subtitle, className = '', icon, onClick, actionLabel }) => {
    const clickable = Boolean(onClick);
    const Wrapper = clickable ? 'button' : 'div';
    return (
      <Wrapper
        type={clickable ? 'button' : undefined}
        onClick={onClick}
        className={`bg-white border border-gray-200 p-6 rounded-xl shadow-lg transition-all duration-200 text-left w-full ${
          clickable ? 'hover:shadow-xl hover:border-teal-300 cursor-pointer group' : 'hover:shadow-xl'
        } ${className}`}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            {icon && (
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                {icon}
              </div>
            )}
            <div>
              <h4 className="text-sm font-medium text-gray-600 uppercase tracking-wide">{title}</h4>
              {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
            </div>
          </div>
          {clickable && (
            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-teal-600 shrink-0" />
          )}
        </div>
        <p className="text-3xl font-bold text-gray-900">{value}</p>
        {actionLabel && (
          <p className="text-xs text-teal-600 font-medium mt-2 group-hover:underline">{actionLabel}</p>
        )}
      </Wrapper>
    );
  };

  const ProgressBar = ({ label, value, max, color = 'bg-teal-500' }) => {
    const percentage = max > 0 ? (value / max) * 100 : 0;
    return (
      <div className="mb-4">
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <span className="text-sm text-gray-500">{value}</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div
            className={`${color} h-2 rounded-full transition-all duration-300`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  const PieChartCard = ({ title, description, data, emptyMessage }) => (
    <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-lg">
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
        {description && <p className="text-gray-500 text-sm">{description}</p>}
      </div>
      {data.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="flex items-center justify-center">
            <div className="w-full max-w-sm">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    innerRadius={20}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="#fff"
                    strokeWidth={2}
                  >
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="flex items-center">
            <div className="w-full grid grid-cols-1 gap-3">
              {data.map((entry, index) => (
                <div
                  key={entry.name}
                  className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div
                    className="w-5 h-5 rounded-full mr-4 flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <div className="flex-1 min-w-0 text-sm font-semibold text-gray-900">{entry.name}</div>
                  <div className="text-sm font-bold text-gray-700 ml-2">{entry.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="h-[280px] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 text-lg">{emptyMessage}</p>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <p className="text-white/60">Loading reports...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout activeSection="REPORTING_OVERVIEW">
        <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-3 rounded">
          {error}
        </div>
      </AdminLayout>
    );
  }

  const entryStatusData = [
    { name: 'Pending', value: reportData.entries.byStatus.pending },
    { name: 'Approved', value: reportData.entries.byStatus.approved },
    { name: 'Rejected', value: reportData.entries.byStatus.rejected },
  ].filter((d) => d.value > 0);

  const campaignStatusData = [
    { name: 'Active', value: reportData.campaigns.active },
    { name: 'Inactive', value: reportData.campaigns.inactive },
  ].filter((d) => d.value > 0);

  return (
    <AdminLayout activeSection="REPORTING_OVERVIEW">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white">REPORTING</h1>
            <p className="text-white/70 text-sm mt-1">System overview and key metrics</p>
          </div>
        </div>

        <section>
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-white mb-2">Campaign Overview</h3>
            <p className="text-white/70 text-sm">Campaign counts, newsletter signups, and status</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard
              title="Total Campaigns"
              value={reportData.campaigns.total}
              subtitle="All campaigns in the system"
            />
            <StatCard
              title="Total Entries"
              value={reportData.campaigns.entries}
              subtitle="Across all campaigns"
            />
            <StatCard
              title="Newsletter Subscribers"
              value={reportData.campaigns.totalNewsletterSubscribers ?? 0}
              subtitle="Users who subscribed to the newsletter"
              className="border-l-4 border-teal-500"
              onClick={() => navigate('/admin/reports/newsletter')}
              actionLabel="View details"
            />
          </div>
        </section>

        <section>
          <PieChartCard
            title="Campaign Status"
            description="Active vs inactive campaigns"
            data={campaignStatusData}
            emptyMessage="No campaign data available"
          />
        </section>

        <section>
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-white mb-2">Entries Analytics</h3>
            <p className="text-white/70 text-sm">Submission volume and review status</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard title="Total Entries" value={reportData.entries.total} subtitle="All time" />
            <StatCard
              title="Today"
              value={reportData.entries.today}
              className="border-l-4 border-blue-500"
            />
            <StatCard
              title="This Week"
              value={reportData.entries.thisWeek}
              className="border-l-4 border-purple-500"
            />
            <StatCard
              title="This Month"
              value={reportData.entries.thisMonth}
              className="border-l-4 border-indigo-500"
            />
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <PieChartCard
            title="Entry Status"
            description="Pending, approved, and rejected entries"
            data={entryStatusData}
            emptyMessage="No entry status data available"
          />
          <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-lg">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Entry Timeline</h3>
              <p className="text-gray-500 text-sm">Relative submission volume</p>
            </div>
            <ProgressBar
              label="Today"
              value={reportData.entries.today}
              max={reportData.entries.thisMonth || 1}
            />
            <ProgressBar
              label="This Week"
              value={reportData.entries.thisWeek}
              max={reportData.entries.thisMonth || 1}
              color="bg-purple-500"
            />
            <ProgressBar
              label="This Month"
              value={reportData.entries.thisMonth}
              max={reportData.entries.total || 1}
              color="bg-indigo-500"
            />
          </div>
        </section>

        <section>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard
              title="Pending Review"
              value={reportData.entries.byStatus.pending}
              className="border-l-4 border-yellow-500"
            />
            <StatCard
              title="Approved"
              value={reportData.entries.byStatus.approved}
              className="border-l-4 border-green-500"
            />
            <StatCard
              title="Rejected"
              value={reportData.entries.byStatus.rejected}
              className="border-l-4 border-red-500"
            />
          </div>
        </section>

        <section>
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-white mb-2">Forms Usage</h3>
            <p className="text-white/70 text-sm">Form creation and submission activity</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard title="Total Forms" value={reportData.forms.total} subtitle="Created" />
            <StatCard
              title="Total Submissions"
              value={reportData.forms.submissions}
              className="border-l-4 border-teal-500"
            />
            <StatCard
              title="Avg. Fields per Form"
              value={reportData.forms.averageFields ? reportData.forms.averageFields.toFixed(1) : '0'}
              className="border-l-4 border-cyan-500"
            />
          </div>

          {reportData.forms.topForms.length > 0 && (
            <div className="mt-6 bg-white border border-gray-200 p-8 rounded-2xl shadow-lg">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Most Used Forms</h3>
                <p className="text-gray-500 text-sm">Forms with the highest submission counts</p>
              </div>
              <div className="space-y-1">
                {reportData.forms.topForms.map((form, index) => (
                  <ProgressBar
                    key={form.id}
                    label={`${index + 1}. ${form.name}`}
                    value={form.usage_count}
                    max={Math.max(...reportData.forms.topForms.map((f) => f.usage_count), 1)}
                  />
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}

export default Reports;
